import EnumValueNotFoundException from '~/errors/EnumValueNotFoundException';

import { log } from '~/utils/logging';

import {
  DATA_SUBSCRIPTION_DATE_FILTER_CLAUSE_FILTER_KEYWORD,
  DATA_SUBSCRIPTION_DATE_FILTER_CLAUSE_VALUE_TYPE,
} from './constants';
import {
  type DataSubscriptionDateFilterClause,
  type DataSubscriptionDateFilterClauseFilterKeywordKeys,
} from './types';

export const DataSubscriptionDateFilterClauseObject = {
  /**
   * Creates a new DataSubscriptionDateFilterClause object.
   * @param {Partial<DataSubscriptionDateFilterClause>} dateFilterClause - The data for initializing the clause.
   * @returns {DataSubscriptionDateFilterClause} The initialized clause object.
   */
  create(dateFilterClause: Partial<DataSubscriptionDateFilterClause> = {}) {
    return {
      decDays: dateFilterClause?.decDays ?? 0,
      filterKeyword:
        dateFilterClause?.filterKeyword ??
        this.FILTER_KEYWORD.DLN_DATE_FROM.KEY,
      incDays: dateFilterClause?.incDays ?? 0,
      value: 'today', // Currently only "today" exists
      valueType: this.VALUE_TYPE,
    };
  },

  FILTER_KEYWORD: DATA_SUBSCRIPTION_DATE_FILTER_CLAUSE_FILTER_KEYWORD,

  /**
   * Converts the date filter clause into a backend-compatible format.
   * @param {DataSubscriptionDateFilterClause} dateFilterClause - The clause to format.
   * @returns {Record<string, any>} The formatted clause object.
   */
  getBackendFormat(dateFilterClause: Record<string, any>) {
    return {
      dec_days: dateFilterClause.decDays,
      filter_keyword: dateFilterClause.filterKeyword,
      inc_days: dateFilterClause.incDays,
      value: dateFilterClause.value,
      value_type: dateFilterClause.valueType,
    };
  },

  /**
   * Retrieves the filter keyword string based on the provided filter key.
   * @param {string} filterKey - The key of the filter.
   * @returns {string | null} The filter keyword string or null if not found.
   */
  getFilterKeywordString(filterKey: string) {
    const filter = Object.keys(
      DataSubscriptionDateFilterClauseObject.FILTER_KEYWORD,
    ).find(
      (key) =>
        this.FILTER_KEYWORD[
          key as DataSubscriptionDateFilterClauseFilterKeywordKeys
        ].KEY === filterKey,
    );

    if (!filter) {
      log.error({
        message: `Invalid filter: ${filterKey}`,
        error: new EnumValueNotFoundException(`Invalid filter: ${filterKey}`),
      });
      return null;
    }

    return this.FILTER_KEYWORD[
      filter as DataSubscriptionDateFilterClauseFilterKeywordKeys
    ].STRING;
  },

  VALUE_TYPE: DATA_SUBSCRIPTION_DATE_FILTER_CLAUSE_VALUE_TYPE,
};
